<template>
  <AdminLayout breadcrumb="Dashboard" @logout="logout">
    <div class="space-y-6">
      <!-- Welcome Section -->
      <div
        class="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 relative overflow-hidden"
      >
        <div
          class="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-red-50 to-transparent rounded-full transform translate-x-32 -translate-y-32"
        ></div>
        <div class="relative">
          <div class="flex items-center justify-between">
            <div>
              <div class="flex items-center space-x-3 mb-4">
                <img
                  src="https://uptodatewebdesign.s3.eu-west-3.amazonaws.com/uploads/icon-1741669842731.png"
                  alt="Selectair"
                  class="w-12 h-12 object-contain"
                />
                <div>
                  <h2 class="text-3xl font-bold text-gray-900">
                    Welkom terug! {{ userEmail.split("@")[0] }}
                  </h2>
                  <p class="text-lg text-gray-600">
                    Gebruik deze functie om uw bestanden te beheren
                  </p>
                </div>
              </div>
              <!-- <p class="text-gray-600 max-w-md">Manage your folders and organize your files with our professional document management system.</p> -->
            </div>
            <button
              @click="showCreateModal = true"
              class="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-8 py-4 rounded-xl text-sm font-semibold flex items-center space-x-3 shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-1"
            >
              <svg
                class="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 4v16m8-8H4"
                ></path>
              </svg>
              <span>Nieuwe Map Maken</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center items-center py-12">
        <svg
          class="animate-spin h-8 w-8 text-blue-600"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          ></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      </div>

      <!-- Folders Section -->
      <div v-if="!loading">
        <div class="flex items-center justify-between mb-6">
          <h3 class="text-lg font-semibold text-gray-900">Jouw Mappen</h3>
          <span class="text-sm text-gray-500"
            >{{ folders.length }} map{{
              folders.length !== 1 ? "pen" : ""
            }}</span
          >
        </div>

        <!-- Folders Grid -->
        <div
          v-if="folders.length > 0"
          class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8"
        >
          <div
            v-for="folder in folders"
            :key="folder.id"
            class="group bg-white rounded-2xl shadow-lg border border-gray-100 hover:shadow-2xl hover:border-red-200 transition-all duration-300 cursor-pointer overflow-hidden transform hover:-translate-y-2"
            @click="selectFolder(folder)"
          >
            <div class="p-8">
              <div class="flex items-start justify-between mb-6">
                <div class="flex items-center space-x-4 flex-1">
                  <div class="flex-shrink-0">
                    <div
                      class="w-16 h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg"
                    >
                      <svg
                        class="w-8 h-8 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
                        ></path>
                      </svg>
                    </div>
                  </div>
                  <div class="flex-1 min-w-0">
                    <h4
                      class="text-xl font-bold text-gray-900 truncate group-hover:text-red-600 transition-colors mb-2"
                    >
                      <!-- Subfolder indicator -->
                      <span v-if="folder.folder_type === 'subfolder'" class="text-sm text-gray-500 font-normal">
                        📁 {{ folder.parent_name }} /
                      </span>
                      {{ folder.name }}
                    </h4>
                    <p
                      class="text-sm text-gray-600 line-clamp-2 leading-relaxed"
                    >
                      {{ folder.description || "Geen beschrijving opgegeven" }}
                    </p>
                  </div>
                </div>
              </div>

              <div class="space-y-4">
                <!-- Quick Stats -->
                <div class="grid grid-cols-2 gap-3 mb-4">
                  <div
                    class="flex items-center space-x-2 p-2 bg-gray-50 rounded-lg"
                  >
                    <div
                      class="w-6 h-6 bg-blue-100 rounded flex items-center justify-center"
                    >
                      <svg
                        class="w-3 h-3 text-blue-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        ></path>
                      </svg>
                    </div>
                    <div>
                      <p class="text-xs text-gray-500">Submappen</p>
                      <p class="text-sm font-semibold text-gray-900">
                        {{ folder.subfolder_count || 0 }}
                      </p>
                    </div>
                  </div>

                  <div
                    class="flex items-center space-x-2 p-2 bg-gray-50 rounded-lg"
                  >
                    <div
                      class="w-6 h-6 bg-green-100 rounded flex items-center justify-center"
                    >
                      <svg
                        class="w-3 h-3 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                        ></path>
                      </svg>
                    </div>
                    <div>
                      <p class="text-xs text-gray-500">Gedeeld</p>
                      <p class="text-sm font-semibold text-gray-900">
                        {{ folder.users?.length || 0 }}
                      </p>
                    </div>
                  </div>
                </div>

                <div class="flex items-center justify-between">
                  <div
                    class="flex items-center space-x-2 text-sm text-gray-500"
                  >
                    <svg
                      class="w-8 h-8"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      ></path>
                    </svg>
                    <span>{{
                      folder.created_by === userEmail
                        ? "Gemaakt door jou"
                        : `Gemaakt door ${folder.created_by.split("@")[0]}`
                    }}</span>
                  </div>

                  <div class="flex space-x-2">
                    <span
                      v-if="folder.folder_type === 'subfolder'"
                      class="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-blue-100 to-blue-200 text-blue-700"
                    >
                      📁 Submap
                    </span>
                    <span
                      v-if="folder.access_level"
                      class="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold"
                      :class="getAccessLevelClass(folder.access_level)"
                    >
                      {{ folder.access_level }}
                    </span>
                    <span
                      v-if="folder.created_by === userEmail"
                      class="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-red-100 to-red-200 text-red-700"
                    >
                      Eigenaar
                    </span>
                  </div>
                </div>

                <div class="pt-4 border-t border-gray-100">
                  <div class="flex items-center justify-between text-sm">
                    <span class="text-gray-500">{{
                      formatDate(folder.created_at)
                    }}</span>
                    <div
                      class="flex items-center space-x-2 text-red-600 group-hover:text-red-700 transition-colors"
                    >
                      <span class="font-medium">Map openen</span>
                      <svg
                        class="w-8 h-8 transform group-hover:translate-x-1 transition-transform"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M9 5l7 7-7 7"
                        ></path>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="text-center py-16">
        <div class="max-w-md mx-auto">
          <div
            class="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center"
          >
            <svg
              class="w-12 h-12 text-blue-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
              ></path>
            </svg>
          </div>
          <h3 class="text-xl font-semibold text-gray-900 mb-2">
            Nog geen mappen
          </h3>
          <p class="text-gray-500 mb-8">
            Maak je eerste map om je bestanden efficiënt te organiseren.
          </p>
          <button
            @click="showCreateModal = true"
            class="inline-flex items-center px-8 py-4 border border-transparent text-base font-semibold rounded-xl text-white bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-1"
          >
            <svg
              class="w-8 h-8 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 4v16m8-8H4"
              ></path>
            </svg>
            Je Eerste Map Maken
          </button>
        </div>
      </div>
    </div>

    <!-- Create Folder Modal -->
    <div
      v-if="showCreateModal"
      class="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4"
    >
      <div class="relative bg-white rounded-xl shadow-xl w-full max-w-[500px]">
        <div class="p-6">
          <div class="flex items-center mb-6">
            <div
              class="w-12 h-12 bg-gradient-to-r from-red-500 to-red-600 rounded-xl flex items-center justify-center mr-4 shadow-lg"
            >
              <svg
                class="w-6 h-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
                ></path>
              </svg>
            </div>
            <h3 class="text-xl font-bold text-gray-900">Nieuwe Map Maken</h3>
          </div>

          <form @submit.prevent="createFolder" class="space-y-4">
            <div>
              <label
                for="folder-name"
                class="block text-sm font-medium text-gray-700 mb-2"
              >
                Mapnaam *
              </label>
              <input
                id="folder-name"
                type="text"
                required
                v-model="newFolder.name"
                class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="Voer mapnaam in"
              />
            </div>

            <div>
              <label
                for="folder-description"
                class="block text-sm font-medium text-gray-700 mb-2"
              >
                Beschrijving
              </label>
              <textarea
                id="folder-description"
                v-model="newFolder.description"
                rows="3"
                class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                placeholder="Voer mapbeschrijving in (optioneel)"
              ></textarea>
            </div>

            <div class="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                @click="cancelCreate"
                class="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Annuleren
              </button>
              <button
                type="submit"
                :disabled="creating"
                class="px-6 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-semibold transition-all duration-200 flex items-center space-x-2"
              >
                <svg
                  v-if="creating"
                  class="animate-spin w-8 h-8"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  ></circle>
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <span>{{ creating ? "Maken..." : "Map Maken" }}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </AdminLayout>
</template>

<script>
import axios from "axios";
import AdminLayout from "@/components/AdminLayout.vue";

export default {
  name: "AdminDashboard",
  components: {
    AdminLayout,
  },
  data() {
    return {
      folders: [],
      loading: true,
      showCreateModal: false,
      creating: false,
      userEmail: "",
      newFolder: {
        name: "",
        description: "",
      },
    };
  },
  computed: {
    shopSettings() {
      return this.$root.shopSettings || {};
    },
    apiUrl() {
      return (
        this.shopSettings?.apiUrl ||
        "https://selectair-vakanties.uptodatesites.com"
      );
    },
  },
  async mounted() {
    await this.checkAuth();
    await this.loadFolders();
  },
  methods: {
    async checkAuth() {
      const token = localStorage.getItem("admin_token");
      const user = localStorage.getItem("admin_user");

      if (!token) {
        this.$router.push("/admin/login");
        return;
      }

      try {
        const response = await axios.get(`${this.apiUrl}/api/admin/verify`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data.success) {
          this.userEmail = response.data.user.email;
        } else {
          this.logout();
        }
      } catch (error) {
        this.logout();
      }
    },
    async loadFolders() {
      this.loading = true;
      try {
        const token = localStorage.getItem("admin_token");
        const response = await axios.get(`${this.apiUrl}/api/admin/folders`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data.success) {
          this.folders = response.data.data;
        }
      } catch (error) {
        console.error("Error loading folders:", error);
      } finally {
        this.loading = false;
      }
    },
    async createFolder() {
      if (!this.newFolder.name.trim()) return;

      this.creating = true;
      try {
        const token = localStorage.getItem("admin_token");
        const response = await axios.post(
          `${this.apiUrl}/api/admin/folders`,
          this.newFolder,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (response.data.success) {
          await this.loadFolders();
          this.cancelCreate();
        }
      } catch (error) {
        console.error("Error creating folder:", error);
        alert(
          "Error creating folder: " +
            (error.response?.data?.message || "Unknown error")
        );
      } finally {
        this.creating = false;
      }
    },
    cancelCreate() {
      this.showCreateModal = false;
      this.newFolder = { name: "", description: "" };
    },
    selectFolder(folder) {
      this.$router.push(`/admin/folders/${folder.id}`);
    },
    logout() {
      localStorage.removeItem("admin_token");
      localStorage.removeItem("admin_user");
      localStorage.removeItem("uptodate_token");
      localStorage.removeItem("currentClient");
      this.$router.push("/admin/login");
    },
    formatDate(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
    getAccessLevelClass(level) {
      const classes = {
        read: "bg-green-100 text-green-700",
        write: "bg-blue-100 text-blue-700",
        admin: "bg-purple-100 text-purple-700",
      };
      return classes[level] || "bg-gray-100 text-gray-700";
    },
  },
};
</script>

<style scoped>
/* Additional custom styles if needed */
</style>
